"use client";
import React, { useState } from "react";
import { Alert, Box, Button, TextField } from "@mui/material";
import { Store, Upload } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useMarketplace } from "../../hooks/useMarketplace";
import { EmptyState } from "../Common/EmptyState";
import { CardSkeletonList } from "../Common/CardSkeleton";
import { PageHeader } from "../Common/PageHeader";
import { Col } from "../Common/Row";
import { MarketplaceRow } from "./MarketplaceRow";

/** Plugins / marketplace page. Uses the shared workspace page layout
 *  (PageHeader + EmptyState + Col of cards) so it matches Schedules /
 *  Knowledge. Filtering is purely client-side over the fetched list. */
export function MarketplaceBrowser({ embedded = false }: { embedded?: boolean }) {
  const {
    packages,
    connectorState,
    install,
    uninstall,
    updateConfig,
    connect,
    disconnect,
    error,
    notice,
    busy,
  } = useMarketplace();
  const navigate = useNavigate();
  const [filter, setFilter] = useState("");

  const needle = filter.toLowerCase();
  const filtered =
    packages === null
      ? []
      : needle
        ? packages.filter(
            (p) =>
              p.name.toLowerCase().includes(needle) ||
              p.display_name.toLowerCase().includes(needle),
          )
        : packages;

  const marketplaceActions = (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      <TextField
        size="small"
        placeholder="Filter…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        sx={{ minWidth: 220 }}
        slotProps={{ htmlInput: { "aria-label": "marketplace-filter" } }}
      />
      <Button
        data-ph="header-publisher"
        variant="outlined"
        startIcon={<Upload size={16} />}
        onClick={() => navigate("/workspace/publisher")}
      >
        Publish plugin
      </Button>
    </Box>
  );

  return (
    <Box aria-label="marketplace-browser">
      {/* Same PageHeader everywhere (was its own ad hoc Stack with no gap/wrap
          when embedded — the title and the filter/publish actions could sit
          flush against each other in the narrower Plugins dialog). */}
      <PageHeader
        title={embedded ? "Browse catalog" : "Plugins"}
        subtitle={
          embedded
            ? undefined
            : "Browse and install plugins — including connectors you authorize via OAuth — that extend what your assistant can do."
        }
        actions={marketplaceActions}
      />

      {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {packages === null && <CardSkeletonList count={3} />}

      {packages !== null && filtered.length === 0 && (
        <EmptyState
          icon={Store}
          title={packages.length === 0 ? "No plugins available" : "No plugins match"}
          description={
            packages.length === 0
              ? "The marketplace is empty or disabled. Available plugins will appear here."
              : "No plugins match your filter. Try a different search."
          }
        />
      )}

      {packages !== null && filtered.length > 0 && (
        <Col gap={1}>
          {filtered.map((p) => (
            <MarketplaceRow
              key={p.name}
              pkg={p}
              onInstall={install}
              onUninstall={uninstall}
              onUpdateConfig={updateConfig}
              isAdmin={true}
              busy={busy === p.name}
              connectorState={connectorState[p.name]}
              onConnect={connect}
              onDisconnect={disconnect}
            />
          ))}
        </Col>
      )}
    </Box>
  );
}
